/**
 * AdminService
 * HTTP client for admin-only API endpoints.
 */

import axios, { AxiosInstance } from 'axios';
import { AuthService } from './AuthService';
import config from '../config';
import type {
  AdminUserRecord,
  AdminUserDetail,
  AdminInvitationCodeDetail,
  AdminGenerateCodesResponse,
  AdminStats,
  GlobalCriterion,
  GlobalCriteriaResponse,
  GlobalCriteriaPublishResponse,
} from '../types';

export class AdminService {
  private axiosInstance: AxiosInstance;
  private authService: AuthService;

  constructor() {
    this.authService = AuthService.getInstance();

    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.axiosInstance.interceptors.request.use(
      async (cfg) => {
        try {
          const token = await this.authService.getValidAccessToken();
          if (token) cfg.headers.Authorization = `Bearer ${token}`;
        } catch {
          // token fetch failure handled by backend
        }
        return cfg;
      },
      (error) => Promise.reject(error)
    );
  }

  async getStats(): Promise<AdminStats> {
    const response = await this.axiosInstance.get<AdminStats>('/api/admin/stats');
    return response.data;
  }

  async getUsers(): Promise<AdminUserRecord[]> {
    const response = await this.axiosInstance.get<AdminUserRecord[]>('/api/admin/users');
    return response.data;
  }

  async getUserDetail(userId: string): Promise<AdminUserDetail> {
    const response = await this.axiosInstance.get<AdminUserDetail>(
      `/api/admin/users/${encodeURIComponent(userId)}`
    );
    return response.data;
  }

  async editUserSubscription(
    userId: string,
    subscriptionType: string,
    creditsRemaining: number,
    showPrice?: boolean | null
  ): Promise<void> {
    const body: Record<string, unknown> = {
      subscription_type: subscriptionType,
      credits_remaining: creditsRemaining,
    };
    if (showPrice != null) {
      body.show_price = showPrice;
    }
    await this.axiosInstance.patch(
      `/api/admin/users/${encodeURIComponent(userId)}/subscription`,
      body
    );
  }

  async getInvitationCodes(): Promise<AdminInvitationCodeDetail[]> {
    const response = await this.axiosInstance.get<AdminInvitationCodeDetail[]>(
      '/api/admin/invitation-codes'
    );
    return response.data;
  }

  async updateCodeNote(code: string, note: string): Promise<void> {
    await this.axiosInstance.patch(
      `/api/admin/invitation-codes/${encodeURIComponent(code)}/note`,
      { note }
    );
  }

  async updateCodeExpiry(code: string, expiresAt: string | null): Promise<void> {
    await this.axiosInstance.patch(
      `/api/admin/invitation-codes/${encodeURIComponent(code)}/expiry`,
      { expires_at: expiresAt }
    );
  }

  async deleteCode(code: string): Promise<void> {
    await this.axiosInstance.delete(
      `/api/admin/invitation-codes/${encodeURIComponent(code)}`
    );
  }

  async generateCodes(tier: string, count: number): Promise<AdminGenerateCodesResponse> {
    const response = await this.axiosInstance.post<{ codes: string[]; tier: string; count: number }>(
      '/api/admin/invitation-codes/generate',
      { tier, count }
    );
    const origin = window.location.origin;
    return {
      ...response.data,
      registration_links: response.data.codes.map(
        (code) => `${origin}/register?code=${encodeURIComponent(code)}`
      ),
    };
  }

  async getGlobalCriteria(): Promise<GlobalCriteriaResponse> {
    const response = await this.axiosInstance.get<GlobalCriteriaResponse>(
      '/api/admin/global-criteria'
    );
    return response.data;
  }

  async updateGlobalCriteria(criteria: GlobalCriterion[]): Promise<GlobalCriteriaPublishResponse> {
    const response = await this.axiosInstance.put<GlobalCriteriaPublishResponse>(
      '/api/admin/global-criteria',
      { criteria }
    );
    return response.data;
  }

}
